import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect } from "react";
import { subirDatos, eliminarDato } from './syncSupabase';

const STORAGE_PROG = 'alqos_programa_auditorias';
const STORAGE_AUD  = 'alqos_auditorias';

const storage = {
  get: (k) => { try { return JSON.parse(localStorage.getItem(k) || '[]'); } catch { return []; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch { return false; } },
};

const MESES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const PROCESOS = ["Pre Prensa","Impresión Offset","Post Prensa","Control de Calidad","Expedición","Recepción MP","Administración","RRHH","Compras","Dirección"];
const NORMAS   = ["ISO 9001:2015","ISO 14001:2015","FSC","FSSC 22000","ISO 45001"];
const FRECUENCIAS = ["Anual","Semestral","Trimestral","Mensual"];

function anioActual() { return new Date().getFullYear(); }

function generarId(lista) {
  return "PROG-" + anioActual() + "-" + String(lista.length + 1).padStart(3,"0");
}

export default function ProgramaAuditorias({ onVolver, oscuro, usuario }) {
  const [items, setItems]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [vista, setVista]       = useState("calendario");
  const [form, setForm]         = useState({ frecuencia:"Anual", norma:"ISO 9001:2015", mes: new Date().getMonth() });


  const bg     = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto  = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde  = oscuro ? "#1e3a8a" : "#eee";
  const navBg  = oscuro ? "#060f30" : "#09184D";
  const sub    = oscuro ? "#9aabd4" : "#808085";
  const esAdmin = usuario?.rol === "Administrador";

  const inp = { width:"100%", padding:"8px 12px", borderRadius:"8px", border:`1px solid ${borde}`, fontSize:"13px", boxSizing:"border-box", marginTop:"4px", background:cardBg, color:texto, fontFamily:"sans-serif" };
  const lbl = { fontSize:"12px", color: oscuro?"#9aabd4":"#555", fontWeight:"600", display:"block", marginTop:"12px" };

  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('programa_auditorias').select('data');
        if (data && data.length > 0) setItems(data.map(r => r.data));
        else setItems(storage.get(STORAGE_PROG));
      } catch { setItems(storage.get(STORAGE_PROG)); }
      setCargando(false);
    }
    cargar();
  }, []);

  if (cargando) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:bg }}>
      <p style={{ color: oscuro?"white":"#09184D", fontSize:"16px" }}>Cargando programa...</p>
    </div>
  );

  const guardar = () => {
    if (!form.proceso?.trim() || !form.auditor?.trim()) { alert("Completá proceso y auditor."); return; }
    const actuales = storage.get(STORAGE_PROG);
    const nuevo = {
      ...form,
      id: Date.now(),
      codigo: generarId(actuales),
      anio: anioActual(),
      estado: "pendiente",
      fechaCreacion: new Date().toISOString(),
      creadoPor: usuario?.nombre || "Sistema",
      auditoriaGenerada: null,
    };
    const nuevos = [...actuales, nuevo];
    storage.set(STORAGE_PROG, nuevos);
    subirDatos(STORAGE_PROG);
    setItems(nuevos);
    setForm({ frecuencia:"Anual", norma:"ISO 9001:2015", mes: new Date().getMonth() });
    setVista("calendario");
  };

  const eliminar = async (id) => {
    const nuevos = items.filter(i => i.id !== id);
    storage.set(STORAGE_PROG, nuevos);
    await eliminarDato(STORAGE_PROG, id);
    setItems(nuevos);
  };

  const ejecutar = (item) => {
    // Crea la auditoría en Auditorias.js automáticamente
    const auds = storage.get(STORAGE_AUD);
    const anio = anioActual();
    const codigo = "AUD-" + anio + "-" + String(auds.length + 1).padStart(3,"0");
    const nuevaAud = {
      id: Date.now(),
      codigo,
      tipo: "Interna",
      norma: item.norma,
      area: item.proceso,
      auditor: item.auditor,
      fecha: new Date().toISOString().slice(0,10),
      estado: "en_curso",
      objetivo: item.objetivo || "",
      alcance: item.alcance || "",
      hallazgos: [],
      fechaCreacion: new Date().toISOString(),
      creadoPor: usuario?.nombre || "Sistema",
      rolCreador: usuario?.rol || "",
      idPrograma: item.codigo,
    };
    const nuevasAuds = [...auds, nuevaAud];
    storage.set(STORAGE_AUD, nuevasAuds);
    subirDatos(STORAGE_AUD);

    // Marcar ítem del programa como ejecutado
    const actualizados = items.map(i => i.id === item.id ? { ...i, estado:"ejecutada", auditoriaGenerada: codigo } : i);
    storage.set(STORAGE_PROG, actualizados);
    subirDatos(STORAGE_PROG);
    setItems(actualizados);
    alert("Auditoría " + codigo + " creada. Abrila desde el módulo Auditorías.");
  };

  const estadoColor = { pendiente:"#3b82f6", ejecutada:"#22c55e", cancelada:"#e63946", postergada:"#f59e0b" };
  const estadoLabel = { pendiente:"📅 Pendiente", ejecutada:"✅ Ejecutada", cancelada:"⛔ Cancelada", postergada:"⏳ Postergada" };

  // Auditorías del año agrupadas por mes
  const porMes = Array.from({length:12}, (_,m) => items.filter(i => Number(i.mes) === m));

  // KPIs
  const kpis = {
    total: items.length,
    pendientes: items.filter(i => i.estado === "pendiente").length,
    ejecutadas: items.filter(i => i.estado === "ejecutada").length,
    pct: items.length > 0 ? Math.round((items.filter(i=>i.estado==="ejecutada").length / items.length)*100) : 0,
  };

  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"sans-serif" }}>
      {/* NAV */}
      <div style={{ background:navBg, padding:"1rem 1.5rem", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <button onClick={onVolver} style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="prog_auditorias" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="prog_auditorias" oscuro={oscuro} />}
          <span style={{ color:"white", fontWeight:"700", fontSize:"16px" }}>📅 Programa de Auditorías {anioActual()}</span>
        </div>
        <div style={{ display:"flex", gap:"8px" }}>
          {["calendario","lista"].map(v => (
            <button key={v} onClick={() => setVista(v)}
              style={{ padding:"6px 12px", background: vista===v ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.1)", border:"none", color:"white", borderRadius:"8px", cursor:"pointer", fontSize:"12px", fontWeight:"600" }}>
              {v === "calendario" ? "📅 Calendario" : "📋 Lista"}
            </button>
          ))}
          {esAdmin && (
            <button onClick={() => setVista("nuevo")}
              style={{ padding:"6px 14px", background:"#7B5BF2", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"13px", fontWeight:"600" }}>
              + Planificar
            </button>
          )}
        </div>
      </div>

      <div style={{ padding:"1rem 1.5rem" }}>
        {/* KPIs */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(130px,1fr))", gap:"10px", marginBottom:"1.5rem" }}>
          {[["Total",kpis.total,"#7B5BF2"],["Pendientes",kpis.pendientes,"#3b82f6"],["Ejecutadas",kpis.ejecutadas,"#22c55e"],["% Cumplimiento",kpis.pct+"%","#22c55e"]].map(([lab,val,col]) => (
            <div key={lab} style={{ background:cardBg, borderRadius:"12px", padding:"1rem", border:`1px solid ${borde}`, textAlign:"center" }}>
              <p style={{ fontSize:"22px", fontWeight:"700", color:col, margin:0 }}>{val}</p>
              <p style={{ fontSize:"11px", color:sub, margin:"4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>

        {/* FORMULARIO NUEVO */}
        {vista === "nuevo" && (
          <div style={{ background:cardBg, borderRadius:"14px", padding:"1.5rem", border:`1px solid ${borde}`, marginBottom:"1rem" }}>
            <h3 style={{ fontSize:"14px", fontWeight:"700", color:"#7B5BF2", marginBottom:"12px" }}>+ Planificar auditoría</h3>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <div>
                <span style={lbl}>Proceso / Área *</span>
                <select value={form.proceso||""} onChange={e=>setForm(f=>({...f,proceso:e.target.value}))} style={inp}>
                  <option value="">Seleccioná...</option>
                  {PROCESOS.map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <span style={lbl}>Norma</span>
                <select value={form.norma} onChange={e=>setForm(f=>({...f,norma:e.target.value}))} style={inp}>
                  {NORMAS.map(n=><option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <span style={lbl}>Auditor/a *</span>
                <input value={form.auditor||""} onChange={e=>setForm(f=>({...f,auditor:e.target.value}))} style={inp} placeholder="Nombre del auditor"/>
              </div>
              <div>
                <span style={lbl}>Mes planificado</span>
                <select value={form.mes} onChange={e=>setForm(f=>({...f,mes:Number(e.target.value)}))} style={inp}>
                  {MESES.map((m,i)=><option key={i} value={i}>{m}</option>)}
                </select>
              </div>
              <div>
                <span style={lbl}>Frecuencia</span>
                <select value={form.frecuencia} onChange={e=>setForm(f=>({...f,frecuencia:e.target.value}))} style={inp}>
                  {FRECUENCIAS.map(f=><option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <span style={lbl}>Objetivo</span>
            <textarea rows={2} value={form.objetivo||""} onChange={e=>setForm(f=>({...f,objetivo:e.target.value}))} style={{...inp,resize:"none"}} placeholder="Objetivo de la auditoría planificada"/>
            <span style={lbl}>Alcance</span>
            <textarea rows={2} value={form.alcance||""} onChange={e=>setForm(f=>({...f,alcance:e.target.value}))} style={{...inp,resize:"none"}} placeholder="Alcance"/>
            <div style={{ display:"flex", gap:"8px", marginTop:"14px" }}>
              <button onClick={guardar} style={{ flex:1, padding:"12px", background:"#7B5BF2", color:"white", border:"none", borderRadius:"10px", cursor:"pointer", fontSize:"14px", fontWeight:"700" }}>Guardar</button>
              <button onClick={()=>setVista("calendario")} style={{ padding:"12px 20px", background:"none", border:`1px solid ${borde}`, borderRadius:"10px", cursor:"pointer", color:texto, fontSize:"13px" }}>Cancelar</button>
            </div>
          </div>
        )}

        {/* VISTA CALENDARIO */}
        {vista === "calendario" && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px,1fr))", gap:"12px" }}>
            {MESES.map((mes, i) => {
              const audsMes = porMes[i];
              const mesActual = new Date().getMonth() === i;
              return (
                <div key={i} style={{ background:cardBg, borderRadius:"14px", padding:"1rem", border:`2px solid ${mesActual ? "#7B5BF2" : borde}`, minHeight:"120px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"8px" }}>
                    <span style={{ fontSize:"13px", fontWeight:"700", color: mesActual ? "#7B5BF2" : texto }}>{mes} {anioActual()}</span>
                    {mesActual && <span style={{ fontSize:"10px", background:"#7B5BF222", color:"#7B5BF2", padding:"2px 6px", borderRadius:"6px", fontWeight:"700" }}>HOY</span>}
                  </div>
                  {audsMes.length === 0
                    ? <p style={{ fontSize:"11px", color:sub, fontStyle:"italic" }}>Sin auditorías</p>
                    : audsMes.map(a => (
                        <div key={a.id} style={{ background: oscuro?"#091840":"#f7f9fd", borderRadius:"8px", padding:"8px", marginBottom:"6px", border:`1px solid ${estadoColor[a.estado]||borde}30` }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                            <div style={{ flex:1 }}>
                              <p style={{ fontSize:"11px", fontWeight:"700", color:"#7B5BF2", margin:"0 0 2px" }}>{a.codigo}</p>
                              <p style={{ fontSize:"11px", color:texto, margin:"0 0 2px" }}>{a.proceso}</p>
                              <p style={{ fontSize:"10px", color:sub, margin:0 }}>{a.norma} · {a.auditor}</p>
                              <span style={{ fontSize:"10px", fontWeight:"700", color:estadoColor[a.estado] }}>{estadoLabel[a.estado]}</span>
                              {a.auditoriaGenerada && <p style={{ fontSize:"10px", color:"#22c55e", margin:"2px 0 0", fontWeight:"600" }}>→ {a.auditoriaGenerada}</p>}
                            </div>
                            {esAdmin && (
                              <div style={{ display:"flex", flexDirection:"column", gap:"4px" }}>
                                {a.estado === "pendiente" && (
                                  <button onClick={()=>ejecutar(a)} style={{ padding:"3px 6px", background:"#22c55e", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontSize:"10px", fontWeight:"700" }}>▶ Ejecutar</button>
                                )}
                                <button onClick={()=>eliminar(a.id)} style={{ background:"none", border:"none", color:"#e63946", cursor:"pointer", fontSize:"14px" }}>×</button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                  }
                </div>
              );
            })}
          </div>
        )}

        {/* VISTA LISTA */}
        {vista === "lista" && (
          <div>
            {items.length === 0
              ? <div style={{ textAlign:"center", padding:"4rem" }}>
                  <p style={{ fontSize:"40px" }}>📅</p>
                  <p style={{ fontSize:"15px", color:texto }}>Sin auditorías planificadas</p>
                  <p style={{ fontSize:"13px", color:sub }}>Usá "+ Planificar" para armar el programa anual</p>
                </div>
              : items.map(a => (
                  <div key={a.id} style={{ background:cardBg, borderRadius:"12px", padding:"1.2rem 1.5rem", border:`1px solid ${estadoColor[a.estado]||borde}40`, marginBottom:"10px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                      <div>
                        <span style={{ fontWeight:"700", color:"#7B5BF2", fontSize:"13px" }}>{a.codigo}</span>
                        <span style={{ marginLeft:"8px", fontSize:"13px", color:texto, fontWeight:"600" }}>{a.proceso}</span>
                        <p style={{ fontSize:"12px", color:sub, marginTop:"4px" }}>{a.norma} · {a.auditor} · {MESES[a.mes]} {anioActual()} · {a.frecuencia}</p>
                        {a.auditoriaGenerada && <p style={{ fontSize:"11px", color:"#22c55e", fontWeight:"600", margin:"4px 0 0" }}>→ Auditoría generada: {a.auditoriaGenerada}</p>}
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"6px" }}>
                        <span style={{ fontSize:"11px", fontWeight:"700", color:estadoColor[a.estado] }}>{estadoLabel[a.estado]}</span>
                        {esAdmin && a.estado === "pendiente" && (
                          <button onClick={()=>ejecutar(a)} style={{ padding:"4px 10px", background:"#22c55e", color:"white", border:"none", borderRadius:"6px", cursor:"pointer", fontSize:"11px", fontWeight:"700" }}>▶ Ejecutar</button>
                        )}
                        {esAdmin && (
                          <button onClick={()=>eliminar(a.id)} style={{ background:"none", border:"none", color:"#e63946", cursor:"pointer", fontSize:"14px" }}>×</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
            }
          </div>
        )}
      </div>
    </div>
  );
}
